import type { DrawBetType, DrawBetTypeConfig } from '@nexbit/types'

/**
 * Starting values for a new lottery's bet types, taken from the ones already running.
 *
 * A blank table of ten bet types with six numbers each is a form nobody fills in
 * correctly; these are the rates and ceilings the live lotteries actually use, so the
 * operator adjusts rather than invents. Every one is editable before saving.
 */

export const BET_TYPE_LABEL: Record<DrawBetType, string> = {
  '3top': '3 ตัวบน',
  '3tote': '3 ตัวโต๊ด',
  '3front': '3 ตัวหน้า',
  '3bottom': '3 ตัวล่าง',
  '2top': '2 ตัวบน',
  '2bottom': '2 ตัวล่าง',
  run_top: 'วิ่งบน',
  run_bottom: 'วิ่งล่าง',
  odd_even_top: 'บน คู่/คี่',
  high_low_top: 'บน สูง/ต่ำ',
  odd_even_bottom: 'ล่าง คู่/คี่',
  high_low_bottom: 'ล่าง สูง/ต่ำ',
}

/** Order the table is drawn in — the ones people bet most, first. */
export const BET_TYPE_ORDER: DrawBetType[] = [
  '3top', '3tote', '3front', '3bottom', '2top', '2bottom', 'run_top', 'run_bottom',
  'odd_even_top', 'high_low_top', 'odd_even_bottom', 'high_low_bottom',
]

const t = (
  enabled: boolean, payout: number, maxBet: number, maxPerNumber: number, maxPerType: number,
): DrawBetTypeConfig => ({ enabled, payout, minBet: 1, maxBet, maxPerNumber, maxPerType })

/**
 * Lotteries that announce a full set of numbers (หวยรัฐบาล, ลาว, ฮานอย…) take every
 * bet type. Rates copied from หวยลาว.
 */
const DRAW_DEFAULTS: Record<DrawBetType, DrawBetTypeConfig> = {
  '3top': t(true, 900, 500, 1_000, 50_000),
  '3tote': t(true, 120, 1_000, 5_000, 50_000),
  // Offered only by the Thai government lottery (it announces two of each), so a new lottery
  // starts with them off.
  '3front': t(false, 400, 500, 1_000, 50_000),
  '3bottom': t(false, 400, 500, 1_000, 50_000),
  '2top': t(true, 90, 2_000, 5_000, 100_000),
  '2bottom': t(true, 90, 2_000, 5_000, 100_000),
  run_top: t(true, 3.2, 5_000, 20_000, 200_000),
  run_bottom: t(true, 4.2, 5_000, 20_000, 200_000),
  odd_even_top: t(true, 1.9, 5_000, 20_000, 200_000),
  high_low_top: t(true, 1.9, 5_000, 20_000, 200_000),
  odd_even_bottom: t(true, 1.9, 5_000, 20_000, 200_000),
  high_low_bottom: t(true, 1.9, 5_000, 20_000, 200_000),
}

/**
 * Stock lotteries announce a 3-digit top and a 2-digit bottom and nothing else, so the
 * derived types (วิ่ง, คู่/คี่, สูง/ต่ำ) start switched off rather than being offered on
 * numbers the market never publishes.
 */
const STOCK_DEFAULTS: Record<DrawBetType, DrawBetTypeConfig> = {
  ...DRAW_DEFAULTS,
  run_top: { ...DRAW_DEFAULTS.run_top, enabled: false },
  run_bottom: { ...DRAW_DEFAULTS.run_bottom, enabled: false },
  odd_even_top: { ...DRAW_DEFAULTS.odd_even_top, enabled: false },
  high_low_top: { ...DRAW_DEFAULTS.high_low_top, enabled: false },
  odd_even_bottom: { ...DRAW_DEFAULTS.odd_even_bottom, enabled: false },
  high_low_bottom: { ...DRAW_DEFAULTS.high_low_bottom, enabled: false },
}

export function defaultBetConfig(category: 'draw' | 'stock'): Record<DrawBetType, DrawBetTypeConfig> {
  return structuredClone(category === 'stock' ? STOCK_DEFAULTS : DRAW_DEFAULTS)
}
